/**
 * Schedules a one-shot watchdog so overlay strategems always send
 * strategem-animation-complete even if onComplete / deferred flush did not run.
 */

import {
  isOverlayStrategem,
  overlayStrategemWatchdogDelayMs,
} from '../config/overlayStrategemTiming';
import {
  deletePendingStrategemAnimInfo,
  normalizeStrategemInstanceKey,
  resolveStrategemAnimationCompleteSend,
} from './strategemTwoPhaseClient';

/**
 * @param {object} p
 * @param {*} p.strategemId
 * @param {string} p.targetType
 * @param {*} p.strategemInstanceId
 * @param {Map} p.pendingMap
 * @param {Set<string>} p.deferredSet
 * @param {boolean} p.wsReady
 * @param {boolean} p.isSpectating
 * @param {boolean} p.isAutobattlerSpectator
 * @param {string} p.gameId
 * @param {(payload: { strategemAnimationId: string, strategemInstanceId: string, gameId: string }) => void} p.sendComplete
 * @returns {number | null} timeout id for clearOverlayStrategemWatchdog
 */
export function scheduleOverlayStrategemCompleteWatchdog({
  strategemId,
  targetType,
  strategemInstanceId,
  pendingMap,
  deferredSet,
  wsReady,
  isSpectating,
  isAutobattlerSpectator,
  gameId,
  sendComplete,
}) {
  if (
    !isOverlayStrategem(strategemId, targetType) ||
    strategemInstanceId == null ||
    !wsReady ||
    isSpectating ||
    isAutobattlerSpectator ||
    !gameId
  ) {
    return null;
  }

  const delayMs = overlayStrategemWatchdogDelayMs(strategemId);
  if (delayMs <= 0) return null;

  const instanceKey = normalizeStrategemInstanceKey(strategemInstanceId);
  if (!instanceKey) return null;

  return window.setTimeout(() => {
    const resolution = resolveStrategemAnimationCompleteSend({
      pendingMap,
      strategemInstanceId,
      wsReady: true,
      isSpectating: false,
      isAutobattlerSpectator: false,
      remainingAnimationsForStrategemCount: 0,
    });
    if (!resolution.send || !resolution.strategemAnimationId) {
      return;
    }
    sendComplete({
      strategemAnimationId: resolution.strategemAnimationId,
      strategemInstanceId: resolution.strategemInstanceId,
      gameId,
    });
    deletePendingStrategemAnimInfo(pendingMap, strategemInstanceId);
    deferredSet.delete(instanceKey);
  }, delayMs);
}

/**
 * @param {Map<string, number>} watchdogTimeoutsByInstanceId
 * @param {*} strategemInstanceId
 */
export function clearOverlayStrategemWatchdog(watchdogTimeoutsByInstanceId, strategemInstanceId) {
  const key = normalizeStrategemInstanceKey(strategemInstanceId);
  if (!key || !watchdogTimeoutsByInstanceId) return;
  const existing = watchdogTimeoutsByInstanceId.get(key);
  if (existing != null) {
    window.clearTimeout(existing);
    watchdogTimeoutsByInstanceId.delete(key);
  }
}
